import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiActivity, FiCalendar, FiDownload, FiGrid, FiList, FiMenu, FiLogOut, FiPackage, FiPlus, FiKey, FiRefreshCcw, FiSave, FiSearch, FiUser, FiUsers, FiX } from "react-icons/fi";
import moment from "moment";
import favicon from "../assets/favicon.png";
import { BASE_URL, hospitalPost } from "../utils/hospitalApi";

const emptyFilters = { patientId: "", mobileNumber: "", fromDate: "", toDate: "" };
const emptyReschedule = { hospital_appointment_id: "", appointment_date: "", old_appointment_time: "", appointment_time: "" };
const PAGE_SIZE = 50;

const emptyEdit = {
  hospital_appointment_id: "",
  appointment_status: "No",
  treatment: "",
  suggested_treatment_amount: "",
  after_discount: "",
  received_amount: "",
  hospital_remarks: "",
  doctor_name: "",
};

const money = (value) => `\u20B9 ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const getTimeInputValue = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["HH:mm", "HH:mm:ss", "h:mm A", "hh:mm A"], true);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
};
const isTreatmentUpdated = (row) => Boolean(
  row.hospital_updated_at ||
  String(row.treatment || "").trim() ||
  String(row.doctor_name || "").trim() ||
  String(row.hospital_remarks || "").trim() ||
  Number(row.received_amount || 0) > 0 ||
  Number(row.suggested_treatment_amount || 0) > 0 ||
  Number(row.after_discount || 0) > 0
);
const canRescheduleAppointment = (row) => {
  const status = String(row.appointment_status || "No").trim().toLowerCase();
  if (status === "yes") return false;
  if (status === "no" || status === "reschedule") return true;
  return !isTreatmentUpdated(row);
};
const formatTime = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["HH:mm", "HH:mm:ss", "h:mm A", "hh:mm A"], true);
  return parsed.isValid() ? parsed.format("hh:mm A") : value;
};

const TableSkeletonRows = ({ rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <tr key={`skeleton-${index}`} className="h-skeleton-row">
        {Array.from({ length: 28 }).map((__, cellIndex) => (
          <td key={cellIndex} className={cellIndex === 0 ? "sticky-index" : cellIndex === 1 ? "sticky-patient" : cellIndex === 2 ? "sticky-name" : ""}>
            <span className={`h-skeleton ${cellIndex === 2 || cellIndex === 18 ? "wide" : cellIndex === 0 ? "tiny" : ""}`} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const JourneySkeleton = () => (
  <div className="h-journey-item">
    <div className="h-journey-top"><span className="h-skeleton wide" /><span className="h-skeleton" /></div>
    <div className="h-journey-meta">
      {Array.from({ length: 7 }).map((_, index) => <div key={index}><span className="h-mini-label"><span className="h-skeleton tiny" /></span><span className="h-skeleton" /></div>)}
    </div>
  </div>
);


const HospitalAppointmentList = () => {
  const navigate = useNavigate();
  const hospitalUserId = localStorage.getItem("hospital_user_id");
  const hospital = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("hospital_user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const profileImage = `${BASE_URL}assets/hospital/${hospital.foto || "user.png"}`;
  const [appointments, setAppointments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(emptyEdit);
  const [filters, setFilters] = useState(emptyFilters);
  const [modalOpen, setModalOpen] = useState(false);
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [patientJourney, setPatientJourney] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [reschedule, setReschedule] = useState(emptyReschedule);
  const [productOpen, setProductOpen] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [productPatient, setProductPatient] = useState(null);
  const [deliveredOrders, setDeliveredOrders] = useState([]);

  const loadAppointments = useCallback(async (nextFilters = emptyFilters) => {
    if (!hospitalUserId) return;
    setLoading(true);
    const res = await hospitalPost("hospital-get-appointments", {
      hospital_user_id: hospitalUserId,
      patient_id: nextFilters.patientId,
      mobile_number: nextFilters.mobileNumber,
      from_date: nextFilters.fromDate,
      to_date: nextFilters.toDate,
    });
    setLoading(false);
    if (res.data.status === 1) {
      setAppointments(res.data.appointments || []);
      setCurrentPage(1);
    }
    else toast.error(res.data.msg || "Unable to load appointments");
  }, [hospitalUserId]);

  useEffect(() => {
    if (!hospitalUserId) {
      navigate("/");
      return;
    }
    loadAppointments(emptyFilters);
  }, [hospitalUserId, navigate, loadAppointments]);

  const logout = () => {
    localStorage.removeItem("hospital_user_id");
    localStorage.removeItem("hospital_user");
    navigate("/");
  };

  const applyFilters = () => loadAppointments(filters);
  const clearFilters = () => {
    setFilters(emptyFilters);
    loadAppointments(emptyFilters);
  };


  const totalPages = Math.max(1, Math.ceil(appointments.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, appointments.length);
  const paginatedAppointments = appointments.slice(pageStart, pageEnd);

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(nextPage);
  };

  const exportToCsv = () => {
    if (!appointments.length) {
      toast.warning("No appointments available to export");
      return;
    }
    const escapeCsv = (value) => {
      const text = String(value ?? "");
      return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const headers = ["#", "Patient ID", "Name", "Mobile", "Gender", "Age", "City", "Created At", "Center Name", "Appointment Date", "Appointment Time", "Source", "Disposition", "Remarks / Issue", "Consult Fee", "Advance", "Transaction ID", "Balance", "Treatment", "Suggested", "After Discount", "Received", "Dr.", "Remarks", "Appointment Status"];
    const rows = appointments.map((row, index) => [
      index + 1,
      row.patient_id,
      row.customer_name,
      row.phone_number,
      row.gender,
      row.age,
      row.city_area,
      row.created_at ? moment(row.created_at).format("DD MMM YYYY hh:mm A") : "-",
      row.center_name || "-",
      row.appointment_date ? moment(row.appointment_date).format("DD MMM YYYY") : "-",
      formatTime(row.appointment_time),
      row.source_name || "-",
      row.disposition === "Other" ? row.disposition_other : row.disposition,
      row.disposition_other || "-",
      Number(row.consultation_fee || 0),
      Number(row.advance_fee || 0),
      row.advance_transaction_id || "-",
      Number(row.pending_fee || 0),
      row.treatment || "-",
      Number(row.suggested_treatment_amount || 0),
      Number(row.after_discount || 0),
      Number(row.received_amount || 0),
      row.doctor_name || "-",
      row.hospital_remarks || "-",
      row.appointment_status || "No",
    ]);
    const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(",")).join("\r\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `hospital-appointments-${moment().format("YYYY-MM-DD-HHmm")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const openJourney = async (row) => {
    setJourneyOpen(true);
    setPatientJourney([]);
    setJourneyLoading(true);
    const res = await hospitalPost("hospital-get-patient-journey", {
      hospital_user_id: hospitalUserId,
      patient_id: row.patient_id,
      mobile_number: row.phone_number,
    });
    setJourneyLoading(false);
    if (res.data.status === 1) setPatientJourney(res.data.journey || []);
    else toast.error(res.data.msg || "Unable to load patient journey");
  };


  const startReschedule = (row) => {
    setReschedule({
      hospital_appointment_id: row.hospital_appointment_id,
      appointment_date: row.appointment_date || "",
      old_appointment_time: row.appointment_time || "",
      appointment_time: getTimeInputValue(row.appointment_time),
    });
    setRescheduleOpen(true);
  };

  const closeReschedule = () => {
    setRescheduleOpen(false);
    setReschedule(emptyReschedule);
  };

  const saveReschedule = async (event) => {
    event.preventDefault();
    if (!reschedule.hospital_appointment_id || !reschedule.appointment_date || !reschedule.appointment_time) {
      toast.warning("Appointment date and time are required");
      return;
    }
    setRescheduleSaving(true);
    const res = await hospitalPost("hospital-reschedule-appointment", { ...reschedule, hospital_user_id: hospitalUserId });
    setRescheduleSaving(false);
    if (res.data.status === 1) {
      toast.success(res.data.msg || "Appointment rescheduled");
      closeReschedule();
      loadAppointments(filters);
    } else {
      toast.error(res.data.msg || "Unable to reschedule appointment");
    }
  };

  const openDeliveredProducts = async (row) => {
    setProductPatient(row);
    setDeliveredOrders([]);
    setProductOpen(true);
    setProductLoading(true);
    const res = await hospitalPost("hospital-get-delivered-products", {
      hospital_user_id: hospitalUserId,
      mobile_number: row.phone_number,
    });
    setProductLoading(false);
    if (res.data.status === 1) setDeliveredOrders(res.data.orders || []);
    else toast.error(res.data.msg || "Unable to load delivered products");
  };
  const startEdit = (row) => {
    setEdit({
      hospital_appointment_id: row.hospital_appointment_id,
      appointment_status: row.appointment_status || "No",
      treatment: row.treatment || "",
      suggested_treatment_amount: row.suggested_treatment_amount || "",
      after_discount: row.after_discount || "",
      received_amount: row.received_amount || "",
      hospital_remarks: row.hospital_remarks || "",
      doctor_name: row.doctor_name || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEdit(emptyEdit);
  };

  const saveTreatment = async (event) => {
    event.preventDefault();
    if (!edit.hospital_appointment_id) {
      toast.warning("Please select an appointment first");
      return;
    }
    setSaving(true);
    const res = await hospitalPost("hospital-update-treatment", { ...edit, hospital_user_id: hospitalUserId });
    setSaving(false);
    if (res.data.status === 1) {
      toast.success(res.data.msg || "Treatment updated");
      closeModal();
      loadAppointments(filters);
    } else {
      toast.error(res.data.msg || "Unable to update treatment");
    }
  };

  return (
    <div className="hospital-shell">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
        .hospital-shell { min-height:100vh; background:#edf3f7; font-family:'Nunito','DM Sans',sans-serif; color:#102033; display:flex; }
        .h-sidebar { width:118px; background:#07182e; color:#fff; min-height:100vh; position:sticky; top:0; align-self:flex-start; box-shadow:10px 0 28px rgba(15,23,42,.10); z-index:5; }
        .h-brand { height:68px; display:flex; align-items:center; justify-content:center; border-bottom:1px solid rgba(255,255,255,.09); }
        .h-brand img { width:34px; height:34px; object-fit:contain; }
        .h-nav { display:flex; flex-direction:column; padding:0; }
        .h-nav button { min-height:84px; border:0; background:transparent; color:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:10px; font-size:13px; font-weight:900; border-bottom:1px solid rgba(255,255,255,.10); cursor:pointer; }
        .h-nav button.is-active, .h-nav button:hover { background:#fff; color:#0f172a; }
        .h-nav button svg { font-size:24px; color:#9ee7dc; }
        .h-nav button.is-active svg, .h-nav button:hover svg { color:#0f172a; }
        .h-sidebar-logout { position:absolute; left:10px; right:10px; bottom:12px; }
        .h-main { flex:1; min-width:0; }
        .h-topbar { height:66px; background:#07182e; color:#fff; display:flex; align-items:center; justify-content:space-between; padding:0 20px; box-shadow:0 6px 18px rgba(15,23,42,.12); }
        .h-top-title { display:flex; align-items:center; gap:8px; font-size:20px; font-weight:900; letter-spacing:.4px; }
        .h-profile-wrap { position:relative; } .h-profile { border:0; background:transparent; color:#fff; display:flex; align-items:center; gap:10px; cursor:pointer; padding:3px 0; } .h-profile-menu { position:absolute; right:0; top:54px; min-width:190px; background:#fff; color:#0f172a; border:1px solid #dbe3ef; border-radius:13px; box-shadow:0 18px 44px rgba(15,23,42,.18); padding:7px; z-index:20; } .h-profile-menu button { width:100%; border:0; background:transparent; color:#0f172a; display:flex; align-items:center; gap:9px; padding:10px 11px; border-radius:10px; font-size:12px; font-weight:900; text-align:left; cursor:pointer; } .h-profile-menu button:hover { background:#eff6ff; color:#0f766e; }
        .h-profile-name { font-size:14px; font-weight:900; text-align:right; }
        .h-profile-sub { font-size:11px; color:#cbd5e1; text-align:right; }
        .h-avatar { width:42px; height:42px; border-radius:50%; background:linear-gradient(135deg,#14b8a6,#0f766e); color:#fff; display:flex; align-items:center; justify-content:center; font-size:16px; font-weight:900; border:3px solid rgba(255,255,255,.16); overflow:hidden; }
        .h-avatar img { width:100%; height:100%; object-fit:cover; }
        .h-content { padding:14px 14px 64px; }
        .h-footer { position:fixed; left:118px; right:0; bottom:0; height:46px; background:#fff; border-top:1px solid #e5e7eb; display:flex; align-items:center; justify-content:center; z-index:4; color:#8a94a6; font-size:12px; font-weight:700; box-shadow:0 -6px 18px rgba(15,23,42,.06); }
        .h-footer strong { color:#f05a00; font-weight:900; }
        .h-page-head { min-height:118px; border-radius:22px; background:linear-gradient(120deg,#133761 0%,#0f766e 72%,#8b6f34 100%); color:#fff; padding:24px; display:flex; align-items:center; justify-content:space-between; gap:16px; margin-bottom:16px; box-shadow:0 18px 45px rgba(15,23,42,.16); }
        .h-kicker { display:inline-flex; padding:7px 12px; border-radius:999px; background:rgba(255,255,255,.15); color:#dcfce7; font-size:11px; font-weight:900; letter-spacing:.8px; text-transform:uppercase; margin-bottom:10px; }
        .h-page-head h1 { margin:0; font-size:28px; line-height:1.1; font-weight:900; }
        .h-page-head p { margin:8px 0 0; color:#dbeafe; font-weight:800; }
        .h-card { background:#fff; border:1px solid #e2e8f0; border-radius:16px; box-shadow:0 8px 26px rgba(15,23,42,.06); margin-bottom:16px; overflow:hidden; }
        .h-card-head { background:#fff; border-bottom:1px solid #e2e8f0; padding:14px 16px; display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap; }
        .h-card-title { font-weight:900; font-size:15px; color:#102033; }
        .h-filter-grid { display:grid; grid-template-columns:repeat(4, minmax(170px, 1fr)) auto auto; gap:10px; padding:14px 16px; align-items:end; background:#f8fafc; border-bottom:1px solid #e2e8f0; }
        .h-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(210px,1fr)); gap:12px; padding:16px; }
        .h-label { display:block; font-size:11px; font-weight:900; color:#64748b; text-transform:uppercase; margin-bottom:5px; }
        .h-input, .h-textarea { width:100%; border:1px solid #dbe3ef; border-radius:9px; font-size:12px; padding:9px 10px; outline:none; background:#fff; }
        .h-textarea { min-height:96px; resize:vertical; }
        .h-input:focus, .h-textarea:focus { border-color:#14b8a6; box-shadow:0 0 0 3px rgba(20,184,166,.11); }
        .h-actions { display:flex; gap:8px; flex-wrap:wrap; padding:0 16px 16px; }
        .h-note { border:1px solid #dbeafe; background:#eff6ff; color:#1e40af; border-radius:12px; padding:12px 14px; font-size:12px; font-weight:800; }
        .h-btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; border:none; border-radius:9px; padding:9px 13px; font-size:12px; font-weight:900; cursor:pointer; white-space:nowrap; }
        .h-btn-primary { background:#0f766e; color:#fff; }
        .h-btn-soft { background:#e6fffb; color:#0f766e; }
        .h-btn-danger { background:#fee2e2; color:#dc2626; }
        .h-table-wrap { overflow-x:auto; overflow-y:visible; }
        .h-table { width:100%; min-width:1980px; border-collapse:separate; border-spacing:0; }
        .h-table th { position:sticky; top:0; z-index:2; background:#07182e; color:#fff; font-size:11px; text-transform:uppercase; padding:12px 9px; text-align:left; white-space:nowrap; }
        .h-table td { border-bottom:1px solid #eef2f7; padding:11px 9px; font-size:12px; white-space:nowrap; vertical-align:middle; color:#0f172a; background:#fff; }
        .h-table tr:nth-child(even) td { background:#f8fafc; }
        .h-table th.sticky-index, .h-table td.sticky-index { position:sticky; left:0; min-width:34px; width:34px; z-index:3; }
        .h-table th.sticky-patient, .h-table td.sticky-patient { position:sticky; left:34px; min-width:96px; width:96px; z-index:3; }
        .h-table th.sticky-name, .h-table td.sticky-name { position:sticky; left:130px; min-width:158px; width:158px; z-index:3; }
        .h-table th.sticky-index, .h-table th.sticky-patient, .h-table th.sticky-name { top:0; z-index:5; background:#07182e; }
        .h-table td.sticky-index, .h-table td.sticky-patient, .h-table td.sticky-name { background:#fff; }
        .h-table tr:nth-child(even) td.sticky-index, .h-table tr:nth-child(even) td.sticky-patient, .h-table tr:nth-child(even) td.sticky-name { background:#f8fafc; }
        .h-pill { display:inline-flex; padding:3px 10px; border-radius:999px; background:#dcfce7; color:#16a34a; font-weight:900; border:1px solid #86efac; }
        .h-appointment-badge { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; background:#e0f2fe; color:#075985; border:1px solid #7dd3fc; font-weight:900; box-shadow:0 4px 12px rgba(14,116,144,.10); }
        .h-appointment-badge svg { flex:0 0 auto; color:#0284c7; }
        .h-status-done { display:inline-flex; padding:6px 10px; border-radius:999px; background:#dcfce7; color:#15803d; border:1px solid #86efac; font-weight:900; }
        .h-amount-blue { color:#0e7490 !important; font-weight:900; }
        .h-amount-red { color:#dc2626 !important; font-weight:900; }
        .h-modal-backdrop { position:fixed; inset:0; background:rgba(7,24,46,.62); z-index:30; display:flex; align-items:center; justify-content:center; padding:18px; }
        .h-modal { width:min(980px,96vw); max-height:90vh; overflow:auto; background:#fff; border-radius:20px; box-shadow:0 30px 80px rgba(0,0,0,.28); border:1px solid #e2e8f0; }
        .h-modal-head { padding:16px 18px; border-bottom:1px solid #e2e8f0; display:flex; align-items:center; justify-content:space-between; gap:12px; }
        .h-modal-title { font-size:18px; font-weight:900; color:#102033; }
        .h-journey-list { display:grid; gap:12px; padding:16px; }
        .h-journey-item { border:1px solid #e2e8f0; border-radius:14px; padding:14px; background:#f8fafc; }
        .h-journey-top { display:flex; justify-content:space-between; gap:12px; flex-wrap:wrap; color:#0f172a; font-weight:900; }
        .h-journey-meta { display:grid; grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); gap:8px; margin-top:10px; color:#475569; font-size:12px; font-weight:800; }
        .h-mini-label { color:#64748b; font-size:10px; text-transform:uppercase; font-weight:900; display:block; }
        .h-icon-btn { width:38px; height:38px; border:0; border-radius:10px; background:#fee2e2; color:#dc2626; display:flex; align-items:center; justify-content:center; cursor:pointer; }
        .h-skeleton { display:block; width:86px; height:13px; border-radius:999px; background:linear-gradient(90deg,#e8eef5 0%,#f8fafc 45%,#e8eef5 90%); background-size:220% 100%; animation:h-shimmer 1.15s ease-in-out infinite; }
        .h-skeleton.wide { width:150px; }
        .h-skeleton.tiny { width:28px; }
        .h-skeleton-row td { height:52px; }
        @keyframes h-shimmer { 0% { background-position:100% 0; } 100% { background-position:-100% 0; } }
        .h-product-list { display:grid; gap:12px; padding:16px; }
        .h-product-order { border:1px solid #e2e8f0; border-radius:14px; background:#f8fafc; overflow:hidden; }
        .h-product-order-head { display:flex; justify-content:space-between; gap:12px; flex-wrap:wrap; padding:12px 14px; background:#fff; border-bottom:1px solid #e2e8f0; font-size:12px; font-weight:900; color:#0f172a; }
        .h-product-table { width:100%; border-collapse:collapse; }
        .h-product-table th { background:#07182e; color:#fff; text-align:left; padding:9px; font-size:10px; text-transform:uppercase; }
        .h-product-table td { padding:9px; border-bottom:1px solid #e2e8f0; font-size:12px; color:#0f172a; }
        .h-empty-state { padding:26px; text-align:center; color:#64748b; font-weight:900; }        .h-pagination { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 16px; border-top:1px solid #e2e8f0; background:#fff; flex-wrap:wrap; }
        .h-page-info { color:#64748b; font-size:12px; font-weight:900; }
        .h-page-controls { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
        .h-page-btn { min-width:34px; height:34px; border:1px solid #dbe3ef; border-radius:9px; background:#fff; color:#0f172a; font-size:12px; font-weight:900; cursor:pointer; }
        .h-page-btn.is-active { background:#07182e; color:#fff; border-color:#07182e; }
        .h-page-btn:disabled { opacity:.45; cursor:not-allowed; }        @media (max-width:980px) { .h-footer { left:0; } .h-sidebar-logout { position:static; flex:0 0 auto; padding:10px; min-width:112px; } .hospital-shell { display:block; } .h-sidebar { position:relative; width:100%; min-height:auto; display:flex; align-items:center; overflow-x:auto; } .h-brand { flex:0 0 96px; width:96px; height:66px; } .h-nav { flex:1; flex-direction:row; overflow-x:auto; } .h-nav button { min-height:66px; min-width:104px; } .h-topbar { height:auto; padding:12px; } .h-page-head { flex-direction:column; align-items:flex-start; } .h-filter-grid { grid-template-columns:1fr 1fr; } }
        @media (max-width:560px) { .h-content { padding:10px; } .h-page-head { padding:20px; border-radius:18px; } .h-page-head h1 { font-size:24px; } .h-profile-name, .h-profile-sub { display:none; } .h-filter-grid { grid-template-columns:1fr; } }
        .h-menu-toggle { display:none; width:38px; height:38px; border:0; border-radius:10px; background:#e6fffb; color:#0f766e; align-items:center; justify-content:center; font-size:20px; cursor:pointer; }
        .h-sidebar-backdrop { display:none; }
        @media (max-width:980px) {
          .h-menu-toggle { display:inline-flex; flex:0 0 auto; }
          .h-topbar { gap:10px; }
          .h-top-title { flex:1; min-width:0; font-size:17px; }
          .hospital-shell { display:block; }
          .h-main { min-width:0; }
          .h-footer { left:0 !important; }
          .h-sidebar { position:fixed !important; left:0; top:0; bottom:0; width:244px !important; min-height:100vh !important; transform:translateX(-105%); transition:transform .22s ease; display:block !important; overflow:auto !important; z-index:60; box-shadow:18px 0 42px rgba(7,24,46,.28); }
          .h-sidebar.is-open { transform:translateX(0); }
          .h-sidebar-backdrop { display:block; position:fixed; inset:0; border:0; background:rgba(7,24,46,.55); z-index:55; }
          .h-brand { width:auto !important; height:76px !important; flex:0 0 auto !important; border-bottom:1px solid rgba(255,255,255,.09) !important; margin:0 !important; }
          .h-nav { flex:0 0 auto !important; flex-direction:column !important; overflow:visible !important; }
          .h-nav button { min-height:62px !important; min-width:0 !important; width:100% !important; flex-direction:row !important; justify-content:flex-start !important; gap:12px !important; padding:0 18px !important; border-left:0 !important; border-bottom:1px solid rgba(255,255,255,.10) !important; }
          .h-nav button svg { font-size:20px !important; }
          .h-sidebar-logout { position:static !important; padding:14px !important; min-width:0 !important; }
          .h-sidebar-logout .h-btn { width:100% !important; min-height:42px; }
        }
      `}</style>

      <aside className={`h-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="h-brand"><img src={favicon} alt="Sat Kartar" /></div>
        <nav className="h-nav">
          <button type="button" onClick={() => navigate("/dashboard")}><FiGrid /> Dashboard</button>
          <button type="button" onClick={() => navigate("/patient-entry")}><FiPlus /> Patient Entry</button>
          <button className="is-active" type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}><FiList /> Appointment List</button>
          <button type="button" onClick={() => navigate("/past-patients")}><FiUsers /> Past Patients</button>
          <button type="button" onClick={() => navigate("/profile")}><FiUser /> My Profile</button>
        </nav>
        <div className="h-sidebar-logout"><button className="h-btn h-btn-danger" onClick={logout}><FiLogOut size={14} /> Logout</button></div>
      </aside>
      {sidebarOpen ? <button className="h-sidebar-backdrop" type="button" aria-label="Close menu" onClick={() => setSidebarOpen(false)} /> : null}

      <main className="h-main">
        <header className="h-topbar">
          <button className="h-menu-toggle" type="button" aria-label="Open menu" onClick={() => setSidebarOpen(true)}><FiMenu /></button>
          <div className="h-top-title"><span style={{ color: "#ff4b21" }}>SAT KARTAR</span><span style={{ color: "#61a5ff" }}>SANJEEVAN</span></div>
          <div className="h-profile-wrap" onMouseLeave={() => setProfileOpen(false)}>
            <button className="h-profile" type="button" onMouseEnter={() => setProfileOpen(true)} onClick={() => setProfileOpen(true)}>
              <div><div className="h-profile-name">{hospital.hospital_name || "Hospital User"}</div><div className="h-profile-sub">Hospital Panel</div></div>
              <div className="h-avatar"><img src={profileImage} alt="Profile" onError={(e) => { e.currentTarget.src = `${BASE_URL}assets/hospital/user.png`; }} /></div>
              
            </button>
            {profileOpen ? <div className="h-profile-menu"><button type="button" onClick={() => navigate("/change-password")}><FiKey /> Change Password</button><button type="button" onClick={() => navigate("/profile")}><FiUser /> My Profile</button></div> : null}
          </div>
        </header>

        <div className="h-content">
          <section className="h-page-head">
            <div>
              <div className="h-kicker">Appointments</div>
              <h1>Patient Appointment List</h1>
              <p>Use filters to fetch old records, then update treatment details from the modal.</p>
            </div>
            <button className="h-btn h-btn-soft" type="button" onClick={() => loadAppointments(filters)}><FiRefreshCcw size={13} /> Refresh</button>
          </section>

          <div className="h-card">
            <div className="h-card-head"><div className="h-card-title">Patient Appointment List</div><div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={{ color: "#64748b", fontSize: 12, fontWeight: 900 }}>{appointments.length} records</div><button type="button" className="h-btn h-btn-soft" onClick={exportToCsv}><FiDownload size={13} /> CSV</button></div></div>
            <div className="h-filter-grid">
              <div><label className="h-label">Patient ID</label><input className="h-input" placeholder="SKJ00001" value={filters.patientId} onChange={(e) => setFilters((p) => ({ ...p, patientId: e.target.value }))} /></div>
              <div><label className="h-label">Mobile Number</label><input className="h-input" placeholder="Customer mobile" value={filters.mobileNumber} onChange={(e) => setFilters((p) => ({ ...p, mobileNumber: e.target.value }))} /></div>
              <div><label className="h-label">From Date</label><input type="date" className="h-input" value={filters.fromDate} onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value }))} /></div>
              <div><label className="h-label">To Date</label><input type="date" className="h-input" value={filters.toDate} onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value }))} /></div>
              <button className="h-btn h-btn-primary" type="button" onClick={applyFilters}><FiSearch size={13} /> Apply</button>
              <button className="h-btn h-btn-soft" type="button" onClick={clearFilters}>Clear</button>
            </div>
            <div className="h-table-wrap">
              <table className="h-table">
                <thead><tr><th className="sticky-index">#</th><th className="sticky-patient">Patient ID</th><th className="sticky-name">Name</th><th>Mobile</th><th>Gender</th><th>Age</th><th>City</th><th>Created At</th><th>Center Name</th><th>Appointment</th><th>Source</th><th>Disposition</th><th>Remarks / Issue</th><th>Consult Fee</th><th>Advance</th><th>Transaction ID</th><th>Balance</th><th>Treatment</th><th>Suggested</th><th>After Discount</th><th>Received</th><th>Dr.</th><th>Remarks</th><th>Appointment Status</th><th>Action</th><th>Reschedule</th><th>Products</th><th>Journey</th></tr></thead>
                <tbody>
                  {loading ? <TableSkeletonRows /> : appointments.length ? paginatedAppointments.map((row, index) => (
                    <tr key={row.hospital_appointment_id}>
                      <td className="sticky-index">{pageStart + index + 1}</td><td className="sticky-patient"><span className="h-pill">{row.patient_id}</span></td><td className="sticky-name"><b>{row.customer_name}</b></td><td>{row.phone_number}</td><td>{row.gender}</td><td>{row.age}</td><td>{row.city_area}</td><td>{row.created_at ? moment(row.created_at).format("DD MMM YYYY hh:mm A") : "-"}</td><td>{row.center_name || "-"}</td><td><span className="h-appointment-badge"><FiCalendar size={13} /> {row.appointment_date ? moment(row.appointment_date).format("DD MMM YYYY") : "-"} {formatTime(row.appointment_time)}</span></td><td>{row.source_name || "-"}</td><td>{row.disposition === "Other" ? row.disposition_other : row.disposition}</td><td style={{ whiteSpace: "normal", minWidth: 200 }}>{row.disposition_other || "-"}</td><td className="h-amount-blue">{money(row.consultation_fee)}</td><td>{money(row.advance_fee)}</td><td>{row.advance_transaction_id || "-"}</td><td className="h-amount-red">{money(row.pending_fee)}</td><td>{row.treatment || "-"}</td><td>{money(row.suggested_treatment_amount)}</td><td>{money(row.after_discount)}</td><td>{money(row.received_amount)}</td><td>{row.doctor_name || "-"}</td><td style={{ whiteSpace: "normal", minWidth: 220 }}>{row.hospital_remarks || "-"}</td><td>{row.appointment_status || "No"}</td><td>{isTreatmentUpdated(row) ? <span className="h-status-done">Updated</span> : <button type="button" className="h-btn h-btn-primary" onClick={() => startEdit(row)}>Update</button>}</td><td>{canRescheduleAppointment(row) ? <button type="button" className="h-btn h-btn-soft" onClick={() => startReschedule(row)}><FiCalendar size={13} /> Reschedule</button> : <span className="h-status-done">Locked</span>}</td><td><button type="button" className="h-btn h-btn-soft" onClick={() => openDeliveredProducts(row)}><FiPackage size={13} /> Products</button></td><td><button type="button" className="h-btn h-btn-soft" onClick={() => openJourney(row)}><FiActivity size={13} /> Journey</button></td>
                    </tr>
                  )) : <tr><td colSpan="28" style={{ textAlign: "center", padding: 26, color: "#64748b", fontWeight: 800 }}>No appointments found</td></tr>}
                </tbody>
              </table>
            </div>
            {!loading && appointments.length ? (
              <div className="h-pagination">
                <div className="h-page-info">Showing {pageStart + 1}-{pageEnd} of {appointments.length} records</div>
                <div className="h-page-controls">
                  <button type="button" className="h-page-btn" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Prev</button>
                  {Array.from({ length: totalPages }).map((_, pageIndex) => {
                    const pageNumber = pageIndex + 1;
                    if (totalPages > 7 && pageNumber !== 1 && pageNumber !== totalPages && Math.abs(pageNumber - currentPage) > 1) {
                      if (pageNumber === 2 && currentPage > 4) return <span key="start-ellipsis" className="h-page-info">...</span>;
                      if (pageNumber === totalPages - 1 && currentPage < totalPages - 3) return <span key="end-ellipsis" className="h-page-info">...</span>;
                      return null;
                    }
                    return <button key={pageNumber} type="button" className={`h-page-btn ${currentPage === pageNumber ? "is-active" : ""}`} onClick={() => goToPage(pageNumber)}>{pageNumber}</button>;
                  })}
                  <button type="button" className="h-page-btn" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <footer className="h-footer"><strong>&copy; 2026 Sat Kartar Life Limited.</strong>&nbsp; All Rights Reserved</footer>
      </main>

      {journeyOpen ? (
        <div className="h-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setJourneyOpen(false); }}>
          <div className="h-modal">
            <div className="h-modal-head"><div className="h-modal-title">Patient Journey</div><button type="button" className="h-icon-btn" onClick={() => setJourneyOpen(false)}><FiX size={20} /></button></div>
            <div className="h-journey-list">
              {journeyLoading ? <><JourneySkeleton /><JourneySkeleton /></> : patientJourney.length ? patientJourney.map((item) => (
                <div className="h-journey-item" key={item.hospital_appointment_id}>
                  <div className="h-journey-top"><span>{item.patient_id} - {item.customer_name}</span><span>{item.appointment_date ? moment(item.appointment_date).format("DD MMM YYYY") : "-"} {formatTime(item.appointment_time)}</span></div>
                  <div className="h-journey-meta">
                    <div><span className="h-mini-label">Mobile</span>{item.phone_number || "-"}</div>
                    <div><span className="h-mini-label">Source</span>{item.source_name || "-"}</div>
                    <div><span className="h-mini-label">Disposition</span>{item.disposition === "Other" ? item.disposition_other : item.disposition || "-"}</div>
                    <div><span className="h-mini-label">Treatment</span>{item.treatment || "-"}</div>
                    <div><span className="h-mini-label">Received</span>{money(item.received_amount)}</div>
                    <div><span className="h-mini-label">Dr.</span>{item.doctor_name || "-"}</div>
                    <div><span className="h-mini-label">Remarks</span>{item.hospital_remarks || "-"}</div>
                  </div>
                </div>
              )) : <div className="h-empty-state">No journey found</div>}
            </div>
          </div>
        </div>
      ) : null}

      {modalOpen ? (
        <div className="h-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeModal(); }}>
          <form className="h-modal" onSubmit={saveTreatment}>
            <div className="h-modal-head"><div className="h-modal-title">Update Treatment Details</div><button type="button" className="h-icon-btn" onClick={closeModal}><FiX size={20} /></button></div>
            <div className="h-grid">
              <div><label className="h-label">Appointment ID</label><input className="h-input" value={edit.hospital_appointment_id} readOnly /></div>
              <div><label className="h-label">Appointment Status</label><select className="h-input" value={edit.appointment_status} onChange={(e) => setEdit((p) => ({ ...p, appointment_status: e.target.value }))}><option value="Yes">Yes</option><option value="No">No</option><option value="Reschedule">Reschedule</option></select></div>
              <div><label className="h-label">Treatment</label><input className="h-input" value={edit.treatment} onChange={(e) => setEdit((p) => ({ ...p, treatment: e.target.value }))} /></div>
              <div><label className="h-label">Suggested Treatment Amount</label><input className="h-input" value={edit.suggested_treatment_amount} onChange={(e) => setEdit((p) => ({ ...p, suggested_treatment_amount: e.target.value }))} /></div>
              <div><label className="h-label">After Discount</label><input className="h-input" value={edit.after_discount} onChange={(e) => setEdit((p) => ({ ...p, after_discount: e.target.value }))} /></div>
              <div><label className="h-label">Received Amount</label><input className="h-input" value={edit.received_amount} onChange={(e) => setEdit((p) => ({ ...p, received_amount: e.target.value }))} /></div>
              <div><label className="h-label">Dr. Name</label><input className="h-input" value={edit.doctor_name} onChange={(e) => setEdit((p) => ({ ...p, doctor_name: e.target.value }))} /></div>
              <div style={{ gridColumn: "1 / -1" }}><label className="h-label">Remarks</label><textarea className="h-textarea" value={edit.hospital_remarks} onChange={(e) => setEdit((p) => ({ ...p, hospital_remarks: e.target.value }))} /></div>
            </div>
            <div className="h-actions"><button className="h-btn h-btn-primary" disabled={saving}><FiSave size={13} /> {saving ? "Saving..." : "Save Treatment"}</button><button type="button" className="h-btn h-btn-soft" onClick={closeModal}>Cancel</button></div>
          </form>
        </div>
      ) : null}
    
      {rescheduleOpen ? (
        <div className="h-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeReschedule(); }}>
          <form className="h-modal" onSubmit={saveReschedule} style={{ width: "min(560px,96vw)" }}>
            <div className="h-modal-head"><div className="h-modal-title">Reschedule Appointment</div><button type="button" className="h-icon-btn" onClick={closeReschedule}><FiX size={20} /></button></div>
            <div className="h-grid">
              <div><label className="h-label">Appointment ID</label><input className="h-input" value={reschedule.hospital_appointment_id} readOnly /></div>
              <div><label className="h-label">Appointment Date</label><input className="h-input" type="date" value={reschedule.appointment_date} onChange={(e) => setReschedule((p) => ({ ...p, appointment_date: e.target.value }))} /></div>
              <div><label className="h-label">Old Appointment Time</label><input className="h-input" value={formatTime(reschedule.old_appointment_time)} readOnly /></div>
              <div><label className="h-label">New Appointment Time</label><input className="h-input" type="time" value={reschedule.appointment_time} onChange={(e) => setReschedule((p) => ({ ...p, appointment_time: e.target.value }))} /></div>

            </div>
            <div className="h-actions"><button className="h-btn h-btn-primary" disabled={rescheduleSaving}><FiCalendar size={13} /> {rescheduleSaving ? "Saving..." : "Save Reschedule"}</button><button type="button" className="h-btn h-btn-soft" onClick={closeReschedule}>Cancel</button></div>
          </form>
        </div>
      ) : null}

      {productOpen ? (
        <div className="h-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setProductOpen(false); }}>
          <div className="h-modal">
            <div className="h-modal-head"><div className="h-modal-title">Delivered Products {productPatient ? `- ${productPatient.patient_id}` : ""}</div><button type="button" className="h-icon-btn" onClick={() => setProductOpen(false)}><FiX size={20} /></button></div>
            <div className="h-product-list">
              {productPatient ? <div className="h-note" style={{ margin: 0 }}>Mobile: {productPatient.phone_number} | Patient: {productPatient.customer_name}</div> : null}
              {productLoading ? <><JourneySkeleton /><JourneySkeleton /></> : deliveredOrders.length ? deliveredOrders.map((order) => (
                <div className="h-product-order" key={order.crm_order_id || order.order_number}>
                  <div className="h-product-order-head">
                    <span>Order: {order.order_number || "-"}</span>
                    <span>Delivery: {order.delivery_date ? moment(order.delivery_date).format("DD MMM YYYY") : "-"}</span>
                    <span>Status: {order.shipping_status || "-"}</span>
                    <span>Total: {money(order.grand_total)}</span>
                  </div>
                  <table className="h-product-table">
                    <thead><tr><th>Product Name</th><th>Qty</th><th>Amount</th></tr></thead>
                    <tbody>
                      {order.products && order.products.length ? order.products.map((product, index) => (
                        <tr key={`${order.order_number}-${index}`}><td>{product.product_name || "-"}</td><td>{product.product_quantity || "-"}</td><td>{money(product.product_total_price)}</td></tr>
                      )) : <tr><td colSpan="3" style={{ textAlign: "center", color: "#64748b", fontWeight: 800 }}>No product lines found</td></tr>}
                    </tbody>
                  </table>
                </div>
              )) : <div className="h-empty-state">No delivered products found for this mobile number</div>}
            </div>
          </div>
        </div>
      ) : null}</div>
  );
};

export default HospitalAppointmentList;









































